import { ApiError } from "@/lib/apiClient";
import {
  PickupLocation,
  CreatePickupLocationDto,
  UpdatePickupLocationDto,
  FetchPickupLocationsOptions,
} from "@/types/models/pickupLocation";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface PickupLocationsState {
  items: PickupLocation[];
  loaded: boolean;
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };
  fetchOptions: FetchPickupLocationsOptions;
  formState: {
    loading: boolean;
    error: ApiError | null;
    success: boolean;
    action: "create" | "update" | "delete" | "set-default" | null;
  };
}

const initialState: PickupLocationsState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  fetchOptions: {
    page: 1,
    page_size: 10,
    sort_by: "created_at",
    sort_order: "desc",
  },
  formState: {
    loading: false,
    error: null,
    success: false,
    action: null,
  },
};

const pickupLocationSlice = createSlice({
  name: "pickupLocations",
  initialState,
  reducers: {
    fetchPickupLocationsRequest: (
      state,
      action: PayloadAction<FetchPickupLocationsOptions>,
    ) => {
      state.loading = true;
      state.error = null;
      state.fetchOptions = { ...state.fetchOptions, ...action.payload };
    },
    fetchPickupLocationsSuccess: (
      state,
      action: PayloadAction<{ data: PickupLocation[]; pagination: any }>,
    ) => {
      state.loading = false;
      state.loaded = true;
      state.items = action.payload.data;
      state.pagination = action.payload.pagination;
    },
    fetchPickupLocationsFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.loaded = true;
      state.error = action.payload;
    },

    createPickupLocationRequest: (
      state,
      _action: PayloadAction<CreatePickupLocationDto>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
      state.formState.action = "create";
    },
    createPickupLocationSuccess: (state, action: PayloadAction<PickupLocation>) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items = [action.payload, ...state.items];
      state.pagination.total += 1;
    },
    createPickupLocationFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    updatePickupLocationRequest: (
      state,
      _action: PayloadAction<{ id: string; data: UpdatePickupLocationDto }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
      state.formState.action = "update";
    },
    updatePickupLocationSuccess: (state, action: PayloadAction<PickupLocation>) => {
      state.formState.loading = false;
      state.formState.success = true;

      const updated = action.payload;
      state.items = state.items.map((item) => {
        if (updated.is_default) {
          if (item.id === updated.id) {
            return updated;
          }
          return { ...item, is_default: false };
        }
        return item.id === updated.id ? updated : item;
      });
    },
    updatePickupLocationFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    deletePickupLocationRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
      state.formState.action = "delete";
    },
    deletePickupLocationSuccess: (state, action: PayloadAction<{ id: string }>) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items = state.items.filter((item) => item.id !== action.payload.id);
      state.pagination.total = Math.max(0, state.pagination.total - 1);
    },
    deletePickupLocationFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    setDefaultPickupLocationRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
      state.formState.action = "set-default";
    },
    setDefaultPickupLocationSuccess: (state, action: PayloadAction<PickupLocation>) => {
      state.formState.loading = false;
      state.formState.success = true;
      const updated = action.payload;
      state.items = state.items.map((item) =>
        item.id === updated.id ? updated : { ...item, is_default: false },
      );
    },
    setDefaultPickupLocationFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    resetFormState: (state) => {
      state.formState = {
        loading: false,
        error: null,
        success: false,
        action: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchPickupLocationsRequest,
  fetchPickupLocationsSuccess,
  fetchPickupLocationsFailure,
  createPickupLocationRequest,
  createPickupLocationSuccess,
  createPickupLocationFailure,
  updatePickupLocationRequest,
  updatePickupLocationSuccess,
  updatePickupLocationFailure,
  deletePickupLocationRequest,
  deletePickupLocationSuccess,
  deletePickupLocationFailure,
  setDefaultPickupLocationRequest,
  setDefaultPickupLocationSuccess,
  setDefaultPickupLocationFailure,
  resetFormState,
} = pickupLocationSlice.actions;

export default pickupLocationSlice.reducer;
